/**
 * Pre-login Property Documents funnel, 8 named events that build the
 * acquisition funnel in Mixpanel.
 *
 *   Step 1  page_view              page=home
 *   Step 2  nav_click               item=property_docs
 *   Step 3  state_select            state, tab            ⚡ high-intent
 *   Step 4  document_select         doc_type
 *   Step 5  form_field_fill         field                (per-field, deduped per session)
 *   Step 6  copy_type_select        type, credits
 *   Step 7  get_it_now_click        doc_type, copy_type, credits
 *   🔒      login_modal_open        trigger, doc_type, step
 *   Step 8  login_complete          method, is_new_user
 *
 * Why a wrapper file vs inline `track()` calls:
 *   - One place to inspect/rename events when marketing tweaks the funnel.
 *   - Each function enforces the expected property shape (typed-by-comment
 *     since this is JS, not TS) so callers can't accidentally pass `state`
 *     where `tab` is expected etc.
 *   - The `form_field_fill` step dedupes within a session, without
 *     deduping, a single form fill could fire dozens of events per
 *     keystroke and blow Mixpanel quota.
 */

import { track } from "./mixpanel";

// ── form-field dedup state ─────────────────────────────────────────────────
// In-memory set of fields the user has already touched this session. Cleared
// when they advance past the form (state_select → resets it because they're
// starting a new doc flow). Each form_field_fill event fires AT MOST ONCE
// per unique field in the funnel, no matter how many keystrokes happen.
let _filledFieldsInSession = new Set();

/** Reset the per-session form-fill tracker. Call on state_select / route change. */
export function resetFormFieldFillTracker() {
  _filledFieldsInSession = new Set();
}

// ── funnel events ──────────────────────────────────────────────────────────

/**
 * Readable label per known page slug. Used to build a PAGE-SPECIFIC event
 * name so the Mixpanel events list shows WHICH page was viewed (e.g.
 * "Home Page Viewed") instead of a single generic "Page Viewed". Anything
 * not listed here is title-cased from its slug as a fallback, so a new
 * `trackPageView("my_orders")` automatically reads "My Orders Page Viewed".
 */
const PAGE_LABELS = {
  home: "Home",
  service_detail: "Service Detail",
  intelligence: "Intelligence",
};

/**
 * Title-case a slug for display in an event name: "doc_no" → "Doc No".
 * Existing acronyms survive untouched ("SRO" → "SRO", "PTIN" → "PTIN")
 * because we only uppercase the first char and keep the rest as-is.
 */
function humanize(slug) {
  return String(slug || "")
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function pageLabel(page) {
  if (!page) return "Page";
  if (PAGE_LABELS[page]) return PAGE_LABELS[page];
  return humanize(page);
}

/**
 * Step 1, user lands on a page. Fires a per-page event name
 * ("Home Page Viewed", "Intelligence Page Viewed", …) AND keeps the raw
 * `page` slug as a property so existing reports that filter on `page`
 * still work and funnels can target the exact page.
 */
export function trackPageView(page, extra = {}) {
  track(`${pageLabel(page)} Page Viewed`, { page, ...extra });
}

/**
 * Generic CTA / action tracker for buttons that aren't one of the named
 * funnel steps above. Use it to measure drop-off on ANY interactive item:
 *
 *   trackButtonClick("Request Document Clicked", { state })
 *   trackButtonClick("Buy Credits Clicked", { amount, location: "dashboard" })
 *
 * `name` is the readable, Title-Case event name shown in Mixpanel (always
 * end it with the action, e.g. "… Clicked"/"… Submitted"). Pass a
 * `location` in props when the same label can appear on more than one page,
 * so the two stay distinguishable in breakdowns. Keeps props PII-free,
 * pass ids/labels, never names/emails/phone (the phone already rides along
 * as a super property from identify()).
 */
export function trackButtonClick(name, props = {}) {
  if (!name) return;
  track(name, props);
}

/** Step 2, top-nav item click. `item` is the canonical id, not the visible label. */
export function trackNavClick(item, extra = {}) {
  track("Nav Clicked", { item, ...extra });
}

/**
 * Step 3, user picked a state. High-intent signal. `state` is the slug
 * (e.g. "telangana"), `tab` is "non-agriculture" | "agriculture".
 */
export function trackStateSelect(state, tab, extra = {}) {
  resetFormFieldFillTracker(); // new flow → reset the form-fill tracker
  track("State Selected", {
    state,
    tab: tab || null,
    intentLevel: "high",
    ...extra,
  });
}

/** Step 4, user clicked a document card. `docType` examples: "EC", "loan_pack", "sale_deed", "rera". */
export function trackDocumentSelect(docType, extra = {}) {
  track("Document Selected", { doc_type: docType, ...extra });
}

/**
 * Step 5, user filled a form field. DEDUPED per session, only the FIRST
 * time the user touches a given field name. Subsequent keystrokes are
 * silent. `field` examples: "SRO", "doc_no", "year", "date", "PTIN", "USN".
 */
export function trackFormFieldFill(field, extra = {}) {
  if (!field) return;
  if (_filledFieldsInSession.has(field)) return;
  _filledFieldsInSession.add(field);
  // Per-field event name so the events list shows WHICH field was filled
  // ("SRO Field Filled", "Year Field Filled") instead of one generic
  // "Form Field Filled". The raw `field` slug is still attached as a
  // property for filtering / backward-compatible reports.
  track(`${humanize(field)} Field Filled`, {
    field,
    fieldsFilledSoFar: _filledFieldsInSession.size,
    ...extra,
  });
}

/**
 * Step 6, user chose soft vs hard copy. `type` is "soft" | "hard",
 * `credits` is the numeric credit cost (99 / 999 etc).
 */
export function trackCopyTypeSelect(type, credits, extra = {}) {
  track("Copy Type Selected", { type, credits, ...extra });
}

/**
 * Step 7, user clicked "Get it Now". `docType` / `copyType` / `credits`
 * are the order context at the moment of click. This is the moment the
 * login gate triggers for anonymous users.
 */
export function trackGetItNowClick({ docType, copyType, credits } = {}, extra = {}) {
  track("Get It Now Clicked", {
    doc_type: docType,
    copy_type: copyType,
    credits,
    ...extra,
  });
}

/**
 * 🔒 LOGIN GATE, login modal opened because the user hit a gated action.
 * `trigger` distinguishes WHY the modal opened (e.g. "get_it_now",
 * "address_select", "credit_purchase"). `step` is the funnel step number
 * that fired the gate (e.g. 7 for get_it_now).
 */
export function trackLoginModalOpen({ trigger, docType, step } = {}, extra = {}) {
  track("Login Modal Opened", {
    trigger,
    doc_type: docType,
    step,
    ...extra,
  });
}

/**
 * Step 8, auth funnel complete. `method` is "otp" | "google" | "email" etc.
 * `isNewUser` distinguishes new-signup from returning-login completion,
 * Mixpanel funnel can branch on this for cohort analysis.
 */
export function trackLoginComplete({ method = "otp", isNewUser } = {}, extra = {}) {
  track("Login Completed", {
    method,
    is_new_user: !!isNewUser,
    ...extra,
  });
}

// ── Property Services funnel (parallel funnel #2) ──────────────────────────
// Shares page_view / nav_click / login_modal_open / login_complete with the
// docs funnel above, but adds two service-specific steps. Reuses the same
// generic `track()` so events land in the same Mixpanel project and can be
// composed into either funnel report without duplicating instrumentation.

/**
 * Services-funnel Step 3, user clicked a specific service tile (e.g.
 * "Title Verification", "PTIN Creation"). `serviceName` is the
 * human-readable label, `serviceId` is the slug used in URLs and lets
 * marketing answer "which service gets most clicks?" without depending
 * on label-translation. `category` distinguishes property-services vs
 * future categories.
 */
export function trackServiceSelect({ serviceName, serviceId, category } = {}, extra = {}) {
  track("Service Selected", {
    service_name: serviceName,
    service_id: serviceId,
    category,
    ...extra,
  });
}

/**
 * Services-funnel Step 5, user clicked "Proceed to Fill Details" on the
 * service detail page. Marks intent to start the form flow; for anonymous
 * users this is also the moment the login gate triggers (the caller fires
 * `trackLoginModalOpen({ trigger: "service_proceed" })` right after).
 */
export function trackServiceFormStart({ serviceId, serviceName, category } = {}, extra = {}) {
  track("Service Form Started", {
    service: serviceId,
    service_name: serviceName,
    category,
    ...extra,
  });
}

// ── Intelligence funnel (parallel funnel #4) ───────────────────────────────
// Shares page_view / nav_click / login_modal_open / login_complete with the
// other funnels; adds the unlock-click and payment-initiate steps. The
// `state` prop on the page_view (locked|unlocked) lets the funnel report
// branch by auth status without a second event.

/**
 * Intelligence-funnel Step 3, user clicked an "Unlock for ₹X" CTA on any
 * gated intelligence surface (single-deed pill, atlas insights overlay,
 * leaderboard reveal, etc.). `price` is the rupee headline shown on the
 * CTA. `action` is the backend action name (intelligenceSearch /
 * intelligenceReport / leaderboardFull / …) so we can answer "which
 * unlock surface converts best?". `surface` distinguishes the inline
 * pill from the full-overlay CTA.
 */
export function trackIntelligenceUnlockClick(
  { price, action, surface, label } = {},
  extra = {},
) {
  track("Intelligence Unlock Clicked", {
    price,
    action,
    surface,
    label,
    ...extra,
  });
}

/**
 * Intelligence-funnel Step 4, payment flow initiated. Fires the moment
 * credits are about to be debited server-side (i.e., the unlock request
 * goes out). `product` is the funnel-bucket label, `amount` is the rupee
 * amount that will be charged. Use `product="intelligence"` for every
 * intelligence-surface charge so the funnel report can roll them up.
 */
export function trackPaymentInitiate({ product, amount, action } = {}, extra = {}) {
  track("Payment Initiated", {
    product,
    amount,
    action,
    ...extra,
  });
}

/**
 * Conversion-complete event, fires AFTER the server confirms the
 * charge succeeded (credits actually debited, paid surface unlocked).
 * Pair with `payment_initiate` to measure payment success rate:
 *   payment_initiate (intent) → payment_success (completed)
 * The delta is where users hit network errors, server rejections, or
 * abandoned the page mid-call. Same `product` taxonomy as
 * `payment_initiate` so the two events join cleanly in a funnel.
 */
export function trackPaymentSuccess({ product, amount, action } = {}, extra = {}) {
  track("Payment Succeeded", {
    product,
    amount,
    action,
    ...extra,
  });
}
